// Unified helper that supports both Promise style and callback style usage
// - Promise: getCurrentPosition(options?) => Promise<GeolocationPosition>
// - Callback: getCurrentPosition(success, error?, options?) => void
//
// Inside the Expo WebView shell, routes through a native bridge
// (postMessage(`geo:request`) → CustomEvent(`witalgeo`)) so the user
// sees a single native prompt instead of WebKit's origin-level prompt
// stacked on top of the iOS system prompt. Normal browsers keep using
// navigator.geolocation unchanged.
declare global {
  interface Window {
    __witalNativeGeo?: boolean
    ReactNativeWebView?: { postMessage(msg: string): void }
  }
}

function isNativeBridgeAvailable(): boolean {
  return typeof window !== 'undefined'
    && window.__witalNativeGeo === true
    && typeof window.ReactNativeWebView?.postMessage === 'function'
}

// Awaits a `witalgeo` CustomEvent whose detail.id matches the request.
// Shapes the response to mirror GeolocationPosition / GeolocationPositionError
// so callers don't need a separate code path.
function requestViaBridge(options: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    const id = `geo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const timeoutMs = options.timeout ?? 15000
    let settled = false

    const cleanup = () => {
      window.removeEventListener('witalgeo', onEvent as EventListener)
      clearTimeout(timer)
    }

    const onEvent = (e: CustomEvent<any>) => {
      const detail = e.detail
      if (!detail || detail.id !== id) return
      if (settled) return
      settled = true
      cleanup()
      if (detail.error) {
        // Mimic GeolocationPositionError: { code, message, PERMISSION_DENIED, ... }
        reject({
          code: detail.error.code ?? 2,
          message: detail.error.message ?? 'Location unavailable',
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3,
        } as GeolocationPositionError)
        return
      }
      resolve({
        coords: detail.coords,
        timestamp: detail.timestamp ?? Date.now(),
      } as GeolocationPosition)
    }

    const timer = setTimeout(() => {
      if (settled) return
      settled = true
      cleanup()
      reject({
        code: 3,
        message: 'Location request timed out.',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
      } as GeolocationPositionError)
    }, timeoutMs)

    window.addEventListener('witalgeo', onEvent as EventListener)
    window.ReactNativeWebView!.postMessage(JSON.stringify({ type: 'geo:request', id }))
  })
}

export async function getCurrentPosition(
  arg1?: any,
  arg2?: any,
  arg3?: any
): Promise<any> {
  const isCallbackStyle = typeof arg1 === 'function'
  // Merge caller options with coarse-friendly defaults
  const callerOptions = (isCallbackStyle ? arg3 : arg1) as PositionOptions | undefined
  const defaultOptions: PositionOptions = {
    enableHighAccuracy: false,
    // Android's first fix after a fresh permission grant can take 10s+ while
    // the location provider warms up. 5s was too tight and fired spurious
    // timeouts on the preview APK. 15s still fails fast if location is truly
    // unavailable (no Wi-Fi + no GPS sky view).
    timeout: 15000,
    maximumAge: 300000
  }
  const mergedOptions: PositionOptions = { ...defaultOptions, ...(callerOptions || {}) }

  if (isNativeBridgeAvailable()) {
    const success: PositionCallback | undefined = isCallbackStyle ? arg1 : undefined
    const error: PositionErrorCallback | undefined = isCallbackStyle ? arg2 : undefined
    const promise = requestViaBridge(mergedOptions)
    if (isCallbackStyle) {
      promise.then(
        (pos) => success?.(pos),
        (err) => error?.(err)
      )
      return
    }
    return promise
  }

  if (isCallbackStyle) {
    const success: PositionCallback = arg1
    const error: PositionErrorCallback | undefined = arg2
    navigator.geolocation.getCurrentPosition(success, error, mergedOptions)
    return
  }

  return new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, mergedOptions)
  })
}
